import "./serviceRequest.css";
import { useState, useEffect } from "react";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm } from "react-hook-form";
import MultipleFileUploader from "../CustomUploader/MultipleFileUploader";
import apiHelper from "../../utils/apiHelper";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  ServiceRequestService,
} from "@propertelligent/client-api";
import { v4 as uuidv4 } from "uuid";

interface VerifyServiceRequestValues {
  id: number;
  documents_verifyDocuments?: File[];
}

interface VerifyServiceRequestFormProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  fetchData?: (page: number) => void;
  response: any;
}

const VerifyServiceRequestForm: React.FC<VerifyServiceRequestFormProps> = ({
  isModalOpen,
  setModalOpen,
  fetchData,
  response,
}) => {
  const [resetCounter, setResetCounter] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const formId = `VerifyServiceRequestForm-${uuidv4()}`;

  const methods = useForm<VerifyServiceRequestValues>({
    defaultValues: {
      id: null as any,
      documents_verifyDocuments: [],
    },
  });

  useEffect(() => {
    if (!isModalOpen || !response) return;

    methods.reset({
      id: response.success?.id || response.id,
      documents_verifyDocuments: [],
    });
  }, [isModalOpen, response, methods]);

  const onSubmit = async (data: VerifyServiceRequestValues) => {
    try {
      setIsLoading(true);

      // Call the verify API
      const verifyResponse = await apiHelper(
        ServiceRequestService.putApiServiceRequestVerify,
        {
          successMessage: "Service request verified successfully",
          showNotification: true,
        },
        data.id
      );

      // Check if verification was successful
      if (!verifyResponse?.data?.success) {
        throw new Error("Verification failed");
      }

      // If there are documents to upload/images, upload them
      if (
        data.documents_verifyDocuments &&
        data.documents_verifyDocuments.length > 0
      ) {
        const uploadPromises = data.documents_verifyDocuments.map((file) => {
          const apiDocObj = {
            EntityTypeId: DocumentEntityTypesEnum.ServiceRequest,
            EntityId: data.id,
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: false,
            FileName: file.name,
            Document: file,
          };
          return apiHelper(
            DocumentService.postApiDocument,
            { showNotification: false },
            apiDocObj
          );
        });

        const uploadResults = await Promise.all(uploadPromises);
      } else {
        console.log("No documents to upload");
      }

      methods.reset();
      setResetCounter((prev) => prev + 1);
      setModalOpen(false);
      if (fetchData) fetchData(1);
    } catch (error) {
      // Show error to user
      alert("Failed to verify service request. Please check console for details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    methods.reset();
    setResetCounter((prev) => prev + 1);
    setModalOpen(false);
  };

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={handleCancel}
      title="Verify Service Request"
      buttonlabel={"Verify"}
      formId={formId}
      maxWidth={550}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <div className="form-group mb-4">
                  <label className="fw-600 mb-2">Service Request Details</label>
                  <div
                    className="p-3"
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    {response?.success?.formatted?.ServiceType && (
                      <div className="mb-2">
                        <strong>Service Type:</strong>{" "}
                        {response.success.formatted.ServiceType}
                      </div>
                    )}
                    {response?.success?.formatted?.ServiceSubType && (
                      <div className="mb-2">
                        <strong>Sub Type:</strong>{" "}
                        {response.success.formatted.ServiceSubType}
                      </div>
                    )}
                    {response?.success?.description && (
                      <div className="mb-2">
                        <strong>Description:</strong>{" "}
                        {response.success.description}
                      </div>
                    )}
                    {response?.success?.streetAddress && (
                      <div className="mb-2">
                        <strong>Address:</strong>{" "}
                        {response.success.streetAddress}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-12">
                <MultipleFileUploader
                  name="documents_verifyDocuments"
                  uploaderId="verifyServiceRequestDocuments"
                  label="Upload Verification Images"
                  disabled={isLoading}
                  allowedFormats={[
                    "image/jpeg",
                    "image/png",
                    "application/pdf",
                  ]}
                  maxSizeMB={5}
                  maxFiles={5}
                  resetTrigger={resetCounter}
                  uploadertext="Upload Files"
                  uploadertypeslabel="Max. size 5MB. JPEG, PNG, PDF (Max 5 files)"
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default VerifyServiceRequestForm;
